import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BetaAgreement = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          {/* NDA Section */}
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Non-Disclosure Agreement (NDA)</h1>
          
          <p className="text-sm text-slate-600 mb-6"><strong>Effective Date:</strong> Immediately</p>
          
          <div className="prose prose-slate max-w-none">
            <p className="mb-6">
              This Non-Disclosure Agreement ("Agreement") is entered into by and between <strong>MKTBOX LLC</strong> ("Company", "we", "us") and the individual or entity accessing or using the beta version of the Company's application ("User", "you").
            </p>
            
            <p className="mb-6">
              By logging into, accessing, or using the beta version of the application (the "Beta App"), you acknowledge that you have read, understood, and agree to be legally bound by this Agreement.
            </p>

            <hr className="my-8 border-slate-300" />

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. Purpose</h2>
            <p className="mb-6">
              The Company is developing proprietary software, systems, features, and technologies related to an AI-powered presentation and depth-notes platform (the "Product"). The Beta App is provided solely for evaluation, testing, and feedback purposes. In connection with this access, you may receive or become aware of Confidential Information belonging to the Company.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. Definition of Confidential Information</h2>
            <p className="mb-4">"Confidential Information" includes, but is not limited to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>The Beta App and all related software, source code, object code, algorithms, workflows, models, prompts, system architecture, APIs, and documentation</li>
              <li>Product features, designs, UI/UX, workflows, wireframes, roadmaps, and unpublished functionality</li>
              <li>AI-related methodologies, training approaches, prompts, fine-tuning strategies, and data processing logic</li>
              <li>Business plans, pricing strategies, go-to-market strategies, user metrics, analytics, and financial information</li>
              <li>Any information identified as confidential or that reasonably should be understood to be confidential given its nature</li>
            </ul>
            <p className="mb-6">Confidential Information includes information disclosed orally, visually, electronically, or in writing.</p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. Exclusions</h2>
            <p className="mb-4">Confidential Information does not include information that:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Is or becomes publicly available through no breach of this Agreement</li>
              <li>Was lawfully known to you prior to disclosure by the Company</li>
              <li>Is independently developed by you without use of the Company's Confidential Information</li>
              <li>Is rightfully obtained from a third party without restriction</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Obligations of User</h2>
            <p className="mb-4">You agree to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Use the Beta App solely for evaluation and testing purposes</li>
              <li>Not disclose, share, publish, reproduce, distribute, or make available any Confidential Information to any third party</li>
              <li>Not reverse engineer, decompile, disassemble, or attempt to derive source code, models, prompts, or system logic</li>
              <li>Not copy, replicate, or create derivative works based on the Beta App or Confidential Information</li>
              <li>Take reasonable measures to protect the confidentiality of the Beta App and Confidential Information</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Intellectual Property Rights</h2>
            <p className="mb-4">
              All rights, title, and interest in and to the Beta App, Product, and all related intellectual property are and shall remain the exclusive property of <strong>MKTBOX LLC</strong>.
            </p>
            <p className="mb-4">
              The Beta App includes <strong>proprietary and patent-pending technology</strong>, including but not limited to software architecture, AI systems, prompt logic, data processing workflows, and presentation synchronization mechanisms.
            </p>
            <p className="mb-4">This Agreement does not grant you any license or rights to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Any issued or pending patents or patent applications</li>
              <li>Trademarks, service marks, trade names, or logos</li>
              <li>Copyrights or trade secrets</li>
            </ul>
            <p className="mb-6">
              You acknowledge that the Beta App contains proprietary technology protected by U.S. and international intellectual property laws. MKTBOX LLC expressly reserves all rights, including the right to seek, maintain, and enforce patent protection and trademark registration now or in the future.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. Feedback</h2>
            <p className="mb-6">
              Any feedback, suggestions, ideas, or comments you provide regarding the Beta App ("Feedback") may be used by the Company without restriction or obligation to you. You hereby grant the Company a perpetual, irrevocable, worldwide, royalty-free right to use, modify, and incorporate such Feedback into the Product.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">7. No Warranty</h2>
            <p className="mb-6">
              The Beta App is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, whether express or implied. The Company does not guarantee that the Beta App will be error-free, uninterrupted, or suitable for any particular purpose.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">8. Term and Survival</h2>
            <p className="mb-4">
              This Agreement becomes effective upon your first access to the Beta App and remains in effect until the Confidential Information becomes public through lawful means.
            </p>
            <p className="mb-6">
              Your obligations regarding confidentiality and intellectual property shall survive termination of your access to the Beta App.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">9. Remedies</h2>
            <p className="mb-6">
              You acknowledge that unauthorized disclosure or misuse of Confidential Information may cause irreparable harm to the Company. The Company shall be entitled to seek injunctive relief, in addition to any other remedies available at law or in equity.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">10. Governing Law</h2>
            <p className="mb-6">
              This Agreement shall be governed by and construed in accordance with the laws of the State of <strong>California</strong>, without regard to conflict of laws principles.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">11. Acceptance by Use</h2>
            <p className="mb-4">By logging into, accessing, or using the Beta App, you:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Acknowledge that you have read and understood this Agreement</li>
              <li>Agree to be bound by its terms</li>
              <li>Represent that you have the legal authority to enter into this Agreement</li>
            </ul>
            <p className="mb-6">
              If you do not agree to this Agreement, you must not access or use the Beta App.
            </p>

            <div className="bg-slate-100 p-6 rounded-lg my-8">
              <p className="mb-2"><strong>Company:</strong> MKTBOX LLC</p>
              <p><strong>Contact:</strong> contact@mkt-box.com</p>
            </div>

            <hr className="my-12 border-slate-300" />

            {/* Terms of Service Section */}
            <h1 className="text-4xl font-bold text-slate-900 mb-4 mt-12">Terms of Service (Beta)</h1>
            
            <p className="text-sm text-slate-600 mb-6"><strong>Effective Date:</strong> Immediately</p>
            
            <p className="mb-6">
              These Terms of Service ("Terms") govern your access to and use of the beta version of the MKTBOX LLC application (the "Beta App"). By accessing or using the Beta App, you agree to be bound by these Terms.
            </p>

            <hr className="my-8 border-slate-300" />

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. Relationship to NDA</h2>
            <p className="mb-4">
              Your use of the Beta App is expressly subject to the <strong>Beta Non-Disclosure Agreement (NDA)</strong>. The NDA is incorporated into these Terms by reference.
            </p>
            <p className="mb-6">
              If there is any conflict between these Terms and the NDA, the NDA shall control with respect to confidentiality, intellectual property, and proprietary technology.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. Eligibility and Access</h2>
            <p className="mb-6">
              You must be at least 18 years old and have the legal capacity to enter into binding agreements to use the Beta App. Access to the Beta App is provided at the sole discretion of MKTBOX LLC and may be revoked at any time.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. Beta Nature of the Service</h2>
            <p className="mb-4">
              The Beta App is provided for evaluation and testing purposes only and may contain bugs, errors, or incomplete features. You acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Features may change or be removed at any time</li>
              <li>Data loss may occur</li>
              <li>Performance is not guaranteed</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Acceptable Use</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Use the Beta App for unlawful purposes</li>
              <li>Reverse engineer, decompile, or attempt to extract source code, models, prompts, or system logic</li>
              <li>Circumvent security, rate limits, or access controls</li>
              <li>Use the Beta App to train competing models or products</li>
              <li>Share access credentials or resell access to the Beta App</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Intellectual Property</h2>
            <p className="mb-6">
              All content, software, technology, and documentation associated with the Beta App are the exclusive property of <strong>MKTBOX LLC</strong> and are protected by copyright, trademark, trade secret, and patent-pending laws.
            </p>
            <p className="mb-6">
              No rights are granted to you except as expressly stated in these Terms and the NDA.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. User Content</h2>
            <p className="mb-6">
              You retain ownership of content you upload to the Beta App. By uploading content, you grant MKTBOX LLC a limited, non-exclusive license to host, process, and display such content solely for the purpose of providing the service.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">7. Feedback</h2>
            <p className="mb-6">
              Any feedback, ideas, or suggestions you provide may be used by MKTBOX LLC without restriction or compensation, as further detailed in the NDA.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">8. Termination</h2>
            <p className="mb-6">
              MKTBOX LLC may suspend or terminate your access to the Beta App at any time, with or without notice, for any reason.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">9. Disclaimer of Warranties</h2>
            <p className="mb-6">
              THE BETA APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">10. Limitation of Liability</h2>
            <p className="mb-6">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MKTBOX LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE BETA APP.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">11. Governing Law</h2>
            <p className="mb-6">
              These Terms shall be governed by and construed in accordance with the laws of the State of <strong>California</strong>, without regard to conflict of laws principles.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">12. Acceptance by Use</h2>
            <p className="mb-6">
              By accessing or using the Beta App, you acknowledge that you have read, understood, and agree to be bound by these Terms and the incorporated NDA.
            </p>

            <div className="bg-slate-100 p-6 rounded-lg my-8">
              <p className="mb-2"><strong>Company:</strong> MKTBOX LLC</p>
              <p className="mb-2"><strong>Contact:</strong> contact@mkt-box.com</p>
              <p className="text-sm text-slate-600"><em>Last updated: Immediately</em></p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-slate-600">
          <p>&copy; 2025 MKTBOX LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BetaAgreement;
